import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Calculator, 
  LogOut, 
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Wallet
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCosts = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [costs, setCosts] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCost, setEditingCost] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    type: "fixed",
    value: 0,
    description: "",
    category: "salary",
    applies_to: [],
    formula: "",
    divisor_months: 1,
    active: true
  });

  useEffect(() => {
    loadCosts();
  }, []);

  const loadCosts = async () => {
    try {
      const response = await axios.get(`${API}/costs?active=true`, { headers: getAuthHeaders() });
      setCosts(response.data);
    } catch (error) {
      toast.error("Erro ao carregar custos");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const openNewDialog = () => {
    setEditingCost(null);
    setFormData({
      name: "",
      type: "fixed",
      value: 0,
      description: "",
      category: "salary",
      applies_to: [],
      divisor_months: 1,
      active: true
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (cost) => {
    setEditingCost(cost);
    setFormData({
      name: cost.name,
      type: cost.type,
      value: cost.value,
      description: cost.description,
      category: cost.category,
      applies_to: cost.applies_to || [],
      formula: cost.formula || "",
      divisor_months: cost.divisor_months || 1,
      active: cost.active
    });
    setIsDialogOpen(true);
  };

  const toggleAppliesTo = (value) => {
    const current = formData.applies_to;
    if (current.includes(value)) {
      setFormData({ ...formData, applies_to: current.filter(v => v !== value) });
    } else {
      setFormData({ ...formData, applies_to: [...current, value] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        value: parseFloat(formData.value) || 0
      };

      if (editingCost) {
        await axios.put(`${API}/costs/${editingCost.id}`, payload, { headers: getAuthHeaders() });
        toast.success("Custo atualizado com sucesso!");
      } else {
        await axios.post(`${API}/costs`, payload, { headers: getAuthHeaders() });
        toast.success("Custo criado com sucesso!");
      }
      setIsDialogOpen(false);
      loadCosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao guardar custo");
    }
  };

  const handleDelete = async (costId) => {
    if (!window.confirm("Tem a certeza que deseja eliminar este custo?")) return;
    try {
      await axios.delete(`${API}/costs/${costId}`, { headers: getAuthHeaders() });
      toast.success("Custo eliminado");
      loadCosts();
    } catch (error) {
      toast.error("Erro ao eliminar custo");
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      fixed: "Fixo (€)",
      percentage: "Percentagem (%)",
      per_employee: "Por Colaborador (€)"
    };
    return labels[type] || type;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      salary: "Salarial",
      operational: "Operacional",
      taxes: "Impostos",
      benefits: "Benefícios"
    };
    return labels[category] || category;
  };

  const getCategoryColor = (category) => {
    const colors = {
      salary: "bg-blue-100 text-blue-800",
      operational: "bg-orange-100 text-orange-800",
      taxes: "bg-red-100 text-red-800",
      benefits: "bg-brand-100 text-brand-800"
    };
    return colors[category] || "bg-gray-100 text-gray-800";
  };

  const formatValue = (cost) => {
    if (cost.type === "percentage") {
      return `${cost.value}%`;
    }
    return `€${cost.value.toFixed(2)}`;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-costs-page">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-600 flex items-center justify-center">
              <Calculator className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="font-manrope font-bold text-lg text-foreground">Evolutt</span>
              <p className="text-xs text-muted-foreground">Custos Operacionais</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Back & Title */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="outline" size="sm" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
            </Link>
            <div>
              <h1 className="font-manrope font-extrabold text-2xl text-foreground">Custos Operacionais</h1>
              <p className="text-muted-foreground">Gerir custos salariais e operacionais</p>
            </div>
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-brand-600 text-white hover:bg-brand-700 font-bold"
                onClick={openNewDialog}
                data-testid="add-cost-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Novo Custo
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-manrope font-bold text-xl">
                  {editingCost ? "Editar Custo" : "Novo Custo"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Nome</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Seguro de Trabalho"
                    data-testid="name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Descrição</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descrição do custo"
                    data-testid="description-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo</Label>
                    <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                      <SelectTrigger data-testid="type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed">Valor Fixo (€)</SelectItem>
                        <SelectItem value="percentage">Percentagem (%)</SelectItem>
                        <SelectItem value="per_employee">Por Colaborador (€)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Valor</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.value}
                      onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                      className="font-mono"
                      data-testid="value-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Categoria</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                    <SelectTrigger data-testid="category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="salary">Salarial</SelectItem>
                      <SelectItem value="operational">Operacional</SelectItem>
                      <SelectItem value="taxes">Impostos</SelectItem>
                      <SelectItem value="benefits">Benefícios</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-3">
                  <Label>Aplica-se a</Label>
                  <div className="flex flex-wrap gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="contract" 
                        checked={formData.applies_to.includes("contract")}
                        onCheckedChange={() => toggleAppliesTo("contract")}
                      />
                      <Label htmlFor="contract" className="font-normal cursor-pointer">Contrato</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="green_receipts" 
                        checked={formData.applies_to.includes("green_receipts")}
                        onCheckedChange={() => toggleAppliesTo("green_receipts")}
                      />
                      <Label htmlFor="green_receipts" className="font-normal cursor-pointer">Recibos Verdes</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="b2b" 
                        checked={formData.applies_to.includes("b2b")}
                        onCheckedChange={() => toggleAppliesTo("b2b")}
                      />
                      <Label htmlFor="b2b" className="font-normal cursor-pointer">B2B</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="perm" 
                        checked={formData.applies_to.includes("perm")}
                        onCheckedChange={() => toggleAppliesTo("perm")}
                        data-testid="applies-to-perm"
                      />
                      <Label htmlFor="perm" className="font-normal cursor-pointer">Perm</Label>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>
                    Fórmula / Regra de cálculo
                    <span className="ml-1 text-[10px] text-muted-foreground font-normal">(informativa — não altera o cálculo)</span>
                  </Label>
                  <Input
                    value={formData.formula}
                    onChange={(e) => setFormData({ ...formData, formula: e.target.value })}
                    placeholder="Ex: salário bruto × 0,4725% ou contabilidade anual / 6 meses"
                    data-testid="formula-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>
                    Diluição (meses)
                    <span className="ml-1 text-[10px] text-muted-foreground font-normal">
                      — divide o &quot;Valor&quot; por N meses. Use 6 para Contabilidade anual paga 2× por ano (default 1).
                    </span>
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    value={formData.divisor_months || 1}
                    onChange={(e) => setFormData({ ...formData, divisor_months: parseInt(e.target.value) || 1 })}
                    placeholder="1"
                    data-testid="divisor-months-input"
                  />
                  {(formData.divisor_months || 1) > 1 && (
                    <p className="text-[11px] text-brand-600">
                      Custo mensal efectivo: {formData.type === "percentage"
                        ? `${(formData.value / (formData.divisor_months || 1)).toFixed(3)}% × bruto`
                        : `${(formData.value / (formData.divisor_months || 1)).toFixed(2)} €`}
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-between pt-2">
                  <Label>Ativo</Label>
                  <Switch
                    checked={formData.active}
                    onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                    data-testid="active-switch"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-primary text-primary-foreground" data-testid="save-btn">
                    {editingCost ? "Guardar Alterações" : "Criar Custo"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Costs List */}
        <Card className="bg-white border border-border/60 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : costs.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <Wallet className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Nenhum custo configurado</p>
                <p className="text-sm text-muted-foreground">Clique em &quot;Novo Custo&quot; ou carregue os dados iniciais</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="font-semibold">Nome</TableHead>
                    <TableHead className="font-semibold">Tipo</TableHead>
                    <TableHead className="font-semibold">Valor</TableHead>
                    <TableHead className="font-semibold">Categoria</TableHead>
                    <TableHead className="font-semibold">Aplica-se a</TableHead>
                    <TableHead className="font-semibold text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {costs.map((cost) => (
                    <TableRow key={cost.id} data-testid={`cost-row-${cost.id}`}>
                      <TableCell className="font-medium">
                        {cost.name}
                        {cost.formula && (
                          <p className="text-[10px] text-muted-foreground font-mono mt-0.5">ƒ {cost.formula}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{getTypeLabel(cost.type)}</TableCell>
                      <TableCell className="font-mono font-medium">{formatValue(cost)}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={getCategoryColor(cost.category)}>
                          {getCategoryLabel(cost.category)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {cost.applies_to?.map((regime) => (
                            <Badge key={regime} variant="outline" className="text-xs">
                              {regime === "contract" ? "Contrato" : regime === "green_receipts" ? "RV" : regime === "perm" ? "Perm" : "B2B"}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => openEditDialog(cost)}
                            data-testid={`edit-btn-${cost.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="text-destructive"
                            onClick={() => handleDelete(cost.id)}
                            data-testid={`delete-btn-${cost.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminCosts;
;
